'use client';

import React, { useState, useCallback } from 'react';
import styles from './ContactForm.module.scss';

export interface ContactFormField {
  name: string;
  label: string;
  type: 'text' | 'email' | 'tel' | 'textarea' | 'select' | 'checkbox' | 'radio';
  required?: boolean;
  placeholder?: string;
  options?: Array<{ value: string; label: string }>;
  validation?: (value: string) => string | null;
}

export interface ContactFormProps {
  title: string;
  description: string;
  fields: ContactFormField[];
  submitText?: string;
  webhookUrl?: string;
  additionalInfo?: {
    title: string;
    content: string[];
  };
  contactInfo?: {
    title: string;
    items: Array<{
      label: string;
      value: string;
      type?: 'email' | 'tel' | 'url' | 'text';
    }>;
  };
}

export function ContactForm({
  title,
  description,
  fields,
  submitText = 'お問い合わせを送信',
  webhookUrl,
  additionalInfo,
  contactInfo,
}: ContactFormProps) {
  const [formData, setFormData] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleInputChange = useCallback((name: string, value: string) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  }, [errors]);

  const validateForm = useCallback(() => {
    const newErrors: Record<string, string> = {};

    fields.forEach(field => {
      const value = formData[field.name] || '';

      if (field.required && !value.trim()) {
        newErrors[field.name] = `${field.label}は必須項目です`;
        return;
      }

      if (field.validation) {
        const error = field.validation(value);
        if (error) {
          newErrors[field.name] = error;
        }
      }

      // 基本的なバリデーション
      if (value.trim()) {
        switch (field.type) {
          case 'email':
            if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
              newErrors[field.name] = '有効なメールアドレスを入力してください';
            }
            break;
          case 'tel':
            if (!/^[\d\-\(\)\+\s]+$/.test(value)) {
              newErrors[field.name] = '有効な電話番号を入力してください';
            }
            break;
        }
      }
    });

    return newErrors;
  }, [fields, formData]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsSubmitting(true);
    setErrors({});

    try {
      if (webhookUrl) {
        // Zapier Webhook送信
        const response = await fetch(webhookUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            timestamp: new Date().toISOString(),
            formType: title,
            ...formData,
          }),
        });

        if (!response.ok) {
          throw new Error('送信に失敗しました');
        }
      }

      setIsSubmitted(true);
      setFormData({});
    } catch (error) {
      console.error('Form submission error:', error);
      setErrors({ submit: '送信に失敗しました。しばらく時間をおいて再度お試しください。' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderField = (field: ContactFormField) => {
    const value = formData[field.name] || '';
    const error = errors[field.name];
    const fieldId = `field-${field.name}`;

    const commonProps = {
      id: fieldId,
      name: field.name,
      value,
      onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => 
        handleInputChange(field.name, e.target.value),
      className: error ? styles.fieldError : '',
      'aria-invalid': !!error,
      'aria-describedby': error ? `${fieldId}-error` : undefined,
    };

    switch (field.type) {
      case 'textarea':
        return (
          <textarea
            {...commonProps}
            placeholder={field.placeholder}
            rows={4}
            className={`${styles.textarea} ${error ? styles.fieldError : ''}`}
          />
        );

      case 'select':
        return (
          <select {...commonProps} className={`${styles.select} ${error ? styles.fieldError : ''}`}>
            <option value="">選択してください</option>
            {field.options?.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        );

      case 'checkbox':
        return (
          <div className={styles.checkboxGroup}>
            {field.options?.map(option => (
              <label key={option.value} className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  name={field.name}
                  value={option.value}
                  checked={value.includes(option.value)}
                  onChange={(e) => {
                    const currentValues = value ? value.split(',') : [];
                    const newValues = e.target.checked
                      ? [...currentValues, option.value]
                      : currentValues.filter(v => v !== option.value);
                    handleInputChange(field.name, newValues.join(','));
                  }}
                  className={styles.checkbox}
                />
                {option.label}
              </label>
            ))}
          </div>
        );

      case 'radio':
        return (
          <div className={styles.radioGroup}>
            {field.options?.map(option => (
              <label key={option.value} className={styles.radioLabel}>
                <input
                  type="radio"
                  name={field.name}
                  value={option.value}
                  checked={value === option.value}
                  onChange={(e) => handleInputChange(field.name, e.target.value)}
                  className={styles.radio}
                />
                {option.label}
              </label>
            ))}
          </div>
        );

      default:
        return (
          <input
            {...commonProps}
            type={field.type}
            placeholder={field.placeholder}
            className={`${styles.input} ${error ? styles.fieldError : ''}`}
          />
        );
    }
  };

  if (isSubmitted) {
    return (
      <div className={styles.container}>
        <div className={styles.successMessage}>
          <div className={styles.successIcon}>✓</div>
          <h2>お問い合わせを受け付けました</h2>
          <p>
            この度はお問い合わせいただき、誠にありがとうございます。<br />
            内容を確認の上、3営業日以内にご連絡いたします。
          </p>
          <button 
            type="button" 
            onClick={() => {
              setIsSubmitted(false);
              setFormData({});
            }}
            className={styles.resetButton}
          >
            新しいお問い合わせを送信
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2 className={styles.title}>{title}</h2>
        <p className={styles.description}>{description}</p>
      </div>

      <div className={styles.content}>
        <form onSubmit={handleSubmit} className={styles.form}>
          {fields.map(field => (
            <div key={field.name} className={styles.fieldGroup}>
              <label htmlFor={`field-${field.name}`} className={styles.label}>
                {field.label}
                {field.required && <span className={styles.required}>*</span>}
              </label>
              {renderField(field)}
              {errors[field.name] && (
                <span id={`field-${field.name}-error`} className={styles.error}>
                  {errors[field.name]}
                </span>
              )}
            </div>
          ))}

          {errors.submit && (
            <div className={styles.submitError}>
              {errors.submit}
            </div>
          )}

          <button
            type="submit"
            disabled={isSubmitting}
            className={styles.submitButton}
          >
            {isSubmitting ? '送信中...' : submitText}
          </button>
        </form>

        {(additionalInfo || contactInfo) && (
          <div className={styles.sidebar}>
            {additionalInfo && (
              <div className={styles.infoSection}>
                <h3 className={styles.infoTitle}>{additionalInfo.title}</h3>
                <div className={styles.infoContent}>
                  {additionalInfo.content.map((item, index) => (
                    <p key={index}>{item}</p>
                  ))}
                </div>
              </div>
            )}

            {contactInfo && (
              <div className={styles.infoSection}>
                <h3 className={styles.infoTitle}>{contactInfo.title}</h3>
                <div className={styles.contactItems}>
                  {contactInfo.items.map((item, index) => (
                    <div key={index} className={styles.contactItem}>
                      <dt className={styles.contactLabel}>{item.label}</dt>
                      <dd className={styles.contactValue}>
                        {item.type === 'email' ? (
                          <a href={`mailto:${item.value}`}>{item.value}</a>
                        ) : item.type === 'tel' ? (
                          <a href={`tel:${item.value}`}>{item.value}</a>
                        ) : item.type === 'url' ? (
                          <a href={item.value} target="_blank" rel="noopener noreferrer">
                            {item.value}
                          </a>
                        ) : (
                          item.value
                        )}
                      </dd>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}